// components/footfall/PulseView.tsx
'use client';

import { useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { ownerOf } from '#lib/footfall/pulse';
import { PulseDashboard } from './PulseDashboard';
import { PulseEditor } from './PulseEditor';
import { blankRuntime, hydrateCentre, loadPlans, openPlan, patchPulse, savedPlans } from './pulseState';

/** Pulse: the plans dashboard, or the open plan's editor. A user or organisation switch starts again from the dashboard. */
export function PulseView() {
  const { kernel } = useKernel();
  const ownerKey = ownerOf(useKernelStore(s => s.userContext));
  const owned = useKernelStore(s => s.pulse.ownerKey) === ownerKey;
  const planOpen = useKernelStore(s => !!s.pulse.planId);
  const user = useKernelStore(s => s.userContext);
  // Plans live on their assets (/api/plan/asset): read once the view is the user's.
  const preferencesReady = useKernelStore(s => s.pulse.plans !== null);
  useEffect(() => { const k = kernel.current; if (k && owned && !k.getState().pulse.plans) void loadPlans(k); }, [kernel, owned]);
  // The plan a restored conversation belongs to (a reload keeps the conversation in the URL, not the open plan).
  const sessionPlanId = useKernelStore(s => {
    const id = s.activeChatSessionId;
    const session = id ? s.inventory.ChatSession.registry[id] : null;
    return session?.creationModule === 'pulse' ? session.creationEntityId ?? null : null;
  });
  useEffect(() => {
    const k = kernel.current;
    if (k && k.getState().pulse.ownerKey !== ownerKey) k.setState({ pulse: blankRuntime(ownerKey) });
  }, [kernel, ownerKey]);
  // The open plan's centre must carry its venue. It is fetched once on open; a failed request, or the centre being
  // replaced in the registry by a lighter copy without it, left the editor on "Loading the plan" for good. So while it
  // is missing it is asked for again, every few seconds, and the user is told when it is taking long.
  const missingVenueOf = useKernelStore(s => {
    const id = s.pulse.planId ? s.pulse.propertyId : null;
    return id && s.inventory.Property.registry[id]?.venue === undefined ? id : null;
  });
  useEffect(() => {
    const k = kernel.current;
    if (!k || !missingVenueOf) return;
    void hydrateCentre(k, missingVenueOf);
    let tries = 0;
    const retry = setInterval(() => {
      tries += 1;
      if (tries === 3) patchPulse(k, { notice: 'The plan is taking longer than usual to load. Still trying.' });
      void hydrateCentre(k, missingVenueOf);
    }, 4000);
    return () => { clearInterval(retry); if (tries >= 3 && k.getState().pulse.notice?.startsWith('The plan is taking')) patchPulse(k, { notice: null }); };
  }, [kernel, missingVenueOf]);
  useEffect(() => {
    const k = kernel.current;
    if (!k || !owned || !preferencesReady || !sessionPlanId || k.getState().pulse.planId) return;
    // A deleted plan leaves its conversation behind: fall back to the dashboard rather than an empty editor.
    if (savedPlans(k)[sessionPlanId]) openPlan(k, sessionPlanId); else k.clearActiveChatSession();
  }, [kernel, owned, preferencesReady, sessionPlanId]);
  if (!owned) return null;
  return planOpen ? <PulseEditor /> : <PulseDashboard />;
}
